import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { 
  Camera, Calendar, ArrowLeft, Sparkles, Loader2, 
  Receipt, Refrigerator, Sun, Sunrise, Moon, 
  ShoppingCart, ChevronRight, Check, X, Image as ImageIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const DAYS = [
  { id: "lundi", label: "Lundi", short: "Lun" },
  { id: "mardi", label: "Mardi", short: "Mar" },
  { id: "mercredi", label: "Mercredi", short: "Mer" },
  { id: "jeudi", label: "Jeudi", short: "Jeu" },
  { id: "vendredi", label: "Vendredi", short: "Ven" },
  { id: "samedi", label: "Samedi", short: "Sam" },
  { id: "dimanche", label: "Dimanche", short: "Dim" },
];

const MEAL_ICONS = {
  petit_dejeuner: { icon: Sunrise, label: "Petit-déjeuner", color: "text-orange-500" },
  dejeuner: { icon: Sun, label: "Déjeuner", color: "text-yellow-500" },
  diner: { icon: Moon, label: "Dîner", color: "text-indigo-500" },
};

const WeeklyMenuPage = () => {
  const [imageType, setImageType] = useState("fridge"); // "fridge" or "receipt"
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [menuData, setMenuData] = useState(null);
  const [selectedDay, setSelectedDay] = useState("lundi");
  const fileInputRef = useRef(null);

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset to allow re-selecting same file
      fileInputRef.current.click();
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image trop grande (max 10MB)");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImage(reader.result);
      setImagePreview(reader.result);
      setMenuData(null);
    };
    reader.onerror = () => {
      toast.error("Erreur lors de la lecture de l'image");
    };
    reader.readAsDataURL(file);
  };

  const generateMenu = async () => {
    if (!image) {
      toast.error("Prenez une photo d'abord");
      return;
    }

    setGenerating(true);

    try {
      const response = await axios.post(`${API}/menu/weekly`, {
        image_base64: image,
        image_type: imageType
      });

      if (response.data.menu) {
        setMenuData(response.data);
        toast.success("Menu généré avec succès !");
      } else {
        toast.error("Erreur lors de la génération du menu");
      }
    } catch (error) {
      console.error("Error generating menu:", error);
      toast.error("Erreur lors de la génération");
    } finally {
      setGenerating(false);
    }
  };

  const resetAll = () => {
    setImage(null);
    setImagePreview(null);
    setMenuData(null);
  };

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-green-500/10 via-emerald-500/5 to-teal-500/10 px-4 pt-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/discover"
            className="inline-flex items-center gap-2 text-charcoal/70 hover:text-charcoal mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-charcoal">
                Menu de la Semaine
              </h1>
              <p className="text-muted-foreground text-sm">
                Généré par IA à partir de vos ingrédients
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 -mt-4 max-w-4xl mx-auto">
        {!menuData ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Source Selection */}
            <Card className="shadow-lg overflow-hidden">
              <CardContent className="p-4">
                <h2 className="font-heading font-semibold mb-4">Choisissez votre source</h2>
                
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setImageType("fridge")}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      imageType === "fridge" 
                        ? "border-green-500 bg-green-50" 
                        : "border-muted hover:border-green-500/50"
                    }`}
                  >
                    <Refrigerator className={`w-8 h-8 mx-auto mb-2 ${imageType === "fridge" ? "text-green-600" : "text-muted-foreground"}`} />
                    <p className={`font-medium ${imageType === "fridge" ? "text-green-700" : "text-charcoal"}`}>
                      Photo du frigo
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Analysez ce que vous avez
                    </p>
                  </button>
                  
                  <button
                    onClick={() => setImageType("receipt")}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      imageType === "receipt" 
                        ? "border-green-500 bg-green-50" 
                        : "border-muted hover:border-green-500/50"
                    }`}
                  >
                    <Receipt className={`w-8 h-8 mx-auto mb-2 ${imageType === "receipt" ? "text-green-600" : "text-muted-foreground"}`} />
                    <p className={`font-medium ${imageType === "receipt" ? "text-green-700" : "text-charcoal"}`}>
                      Ticket de caisse
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Planifiez vos achats
                    </p>
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Image Capture */}
            <Card className="shadow-lg overflow-hidden">
              <CardContent className="p-0">
                {imagePreview ? (
                  <div className="relative">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full aspect-[4/3] object-cover"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                      onClick={resetAll}
                    >
                      <X className="w-5 h-5" />
                    </Button>
                    {generating && (
                      <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center">
                        <Loader2 className="w-12 h-12 text-white animate-spin mb-4" />
                        <p className="text-white font-medium">Génération du menu...</p>
                        <p className="text-white/60 text-sm mt-1">Cela peut prendre quelques secondes</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <label 
                    htmlFor="menu-image-input"
                    className="aspect-[4/3] flex flex-col items-center justify-center bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                  >
                    <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
                      {imageType === "receipt" ? (
                        <Receipt className="w-10 h-10 text-green-600" />
                      ) : (
                        <Camera className="w-10 h-10 text-green-600" />
                      )}
                    </div>
                    <p className="font-medium text-charcoal">
                      {imageType === "receipt" ? "Photographier le ticket" : "Photographier le frigo"}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      ou sélectionner depuis la galerie
                    </p>
                  </label>
                )}

                <input
                  id="menu-image-input"
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="sr-only"
                />
              </CardContent>
            </Card>

            {/* Action Button */}
            <div className="space-y-3">
              {!imagePreview ? (
                <label
                  htmlFor="menu-image-input"
                  className="w-full h-14 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-lg gap-2 flex items-center justify-center text-white font-medium cursor-pointer"
                >
                  <Camera className="w-5 h-5" />
                  {imageType === "receipt" ? "Photographier le ticket" : "Photographier mon frigo"}
                </label>
              ) : (
                <Button
                  onClick={generateMenu}
                  disabled={generating}
                  className="w-full h-14 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-lg gap-2"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Génération en cours...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Générer mon menu de la semaine
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Tips */}
            <Card className="bg-green-500/5 border-green-500/20">
              <CardContent className="p-4">
                <h3 className="font-medium text-charcoal flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-green-600" />
                  Comment ça marche ?
                </h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• L'IA analyse votre photo pour identifier les ingrédients</li>
                  <li>• Elle génère un menu équilibré pour 7 jours</li>
                  <li>• Petit-déjeuner, déjeuner et dîner pour chaque jour</li>
                  <li>• Suggestions d'achats complémentaires incluses</li>
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          /* Menu Results */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Detected Ingredients */}
            {menuData.available_ingredients?.length > 0 && (
              <Card className="shadow-lg">
                <CardContent className="p-4">
                  <h2 className="font-heading font-semibold mb-3 flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    Ingrédients détectés
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {menuData.available_ingredients.map((ing, index) => (
                      <Badge 
                        key={index} 
                        variant="outline"
                        className="py-1 px-2.5 bg-white border-green-500/30 text-charcoal"
                      >
                        {ing}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Day Selector */}
            <div className="flex gap-1 overflow-x-auto pb-2 -mx-4 px-4">
              {DAYS.map((day) => (
                <button
                  key={day.id}
                  onClick={() => setSelectedDay(day.id)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full font-medium text-sm transition-all ${
                    selectedDay === day.id
                      ? "bg-green-500 text-white"
                      : "bg-muted/50 text-muted-foreground hover:bg-muted"
                  }`}
                >
                  <span className="hidden sm:inline">{day.label}</span>
                  <span className="sm:hidden">{day.short}</span>
                </button>
              ))}
            </div>

            {/* Day Menu */}
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedDay}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-3"
              >
                {menuData.menu?.[selectedDay] && Object.entries(menuData.menu[selectedDay]).map(([mealKey, meal]) => {
                  const mealInfo = MEAL_ICONS[mealKey];
                  if (!mealInfo || !meal) return null;
                  const MealIcon = mealInfo.icon;
                  
                  return (
                    <Card key={mealKey} className="shadow-md overflow-hidden">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center flex-shrink-0 ${mealInfo.color}`}>
                            <MealIcon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">
                              {mealInfo.label}
                            </p>
                            <h3 className="font-heading font-semibold text-charcoal mt-0.5">
                              {meal.nom}
                            </h3>
                            {meal.description && (
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                {meal.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </motion.div>
            </AnimatePresence>

            {/* Shopping Suggestions */}
            {menuData.shopping_suggestions?.length > 0 && (
              <Card className="shadow-lg bg-amber-50/50 border-amber-200">
                <CardContent className="p-4">
                  <h2 className="font-heading font-semibold mb-3 flex items-center gap-2 text-amber-800">
                    <ShoppingCart className="w-5 h-5" />
                    Suggestions d'achats
                  </h2>
                  <ul className="space-y-2">
                    {menuData.shopping_suggestions.map((suggestion, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-amber-900">
                        <ChevronRight className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={resetAll}
                className="flex-1 h-12 rounded-xl"
              >
                Recommencer
              </Button>
              <Button
                onClick={() => {
                  // Copy menu to clipboard
                  const menuText = DAYS.map(day => {
                    const dayMenu = menuData.menu?.[day.id];
                    if (!dayMenu) return '';
                    return `📅 ${day.label}\n` +
                      `  🌅 ${dayMenu.petit_dejeuner?.nom || '-'}\n` +
                      `  ☀️ ${dayMenu.dejeuner?.nom || '-'}\n` +
                      `  🌙 ${dayMenu.diner?.nom || '-'}`;
                  }).join('\n\n');
                  navigator.clipboard.writeText(menuText);
                  toast.success("Menu copié !");
                }}
                className="flex-1 h-12 rounded-xl bg-green-500 hover:bg-green-600"
              >
                Copier le menu
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default WeeklyMenuPage;
